import { useEffect, useState, type FormEvent } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconBug,
  IconExternalLink,
  IconPencil,
  IconPlayerStop,
  IconTrash,
} from "@tabler/icons-react";
import {
  apiDelete,
  apiFetch,
  apiPatch,
  apiPost,
  friendlyError,
  type ApiError,
} from "../../api/client.js";
import { useAsync } from "../../lib/useAsync.js";
import { StatusPill } from "../../components/StatusPill.js";
import { appStatusCopy } from "../customer/appUtils.js";
import {
  APPS_DOMAIN,
  type AdminAppHost,
  type AdminAppInstance,
  type AdminOrphansResponse,
} from "./adminAppUtils.js";

const INSTANCES_POLL_MS = 5000;

/** Backend timestamps are SQLite UTC "YYYY-MM-DD HH:MM:SS" — normalize. */
function parseBackendTime(raw: string): Date | null {
  const s = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(" ", "T")}Z`
    : raw;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const shortDtFmt = new Intl.DateTimeFormat(undefined, {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "Pacific/Auckland",
});

function formatShortDateTime(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = parseBackendTime(raw);
  return d ? shortDtFmt.format(d) : raw;
}

function adminErrorToast(err: unknown, fallback: string): void {
  notifications.show({ color: "red", message: friendlyError(err, fallback) });
}

export function Apps() {
  const [activeTab, setActiveTab] = useState<string | null>("hosts");
  return (
    <Box>
      <Title order={2} mb="md">
        Apps
      </Title>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="hosts">Hosts</Tabs.Tab>
          <Tabs.Tab value="instances">Instances</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="hosts">
          <HostsTab />
        </Tabs.Panel>
        <Tabs.Panel value="instances">
          <InstancesTab active={activeTab === "instances"} />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Hosts tab
// ---------------------------------------------------------------------------

// Mirrors CreateAppHostBody/PatchAppHostBody validation (routes/admin.ts).
const HOST_NAME_RE = /^[A-Za-z0-9._-]{1,64}$/;
const SSH_HOST_RE = /^[A-Za-z0-9._-]{1,253}$/;
const SSH_USER_RE = /^[a-zA-Z0-9._-]{1,32}$/;
const SSH_KEY_PATH_RE = /^\/\S{1,255}$/;

interface HostForm {
  name: string;
  ssh_host: string;
  ssh_port: string;
  ssh_user: string;
  ssh_key_path: string;
  pve_vmid: string;
  capacity_mem_mb: string;
  enabled: boolean;
}

function hostFormFrom(h: AdminAppHost): HostForm {
  return {
    name: h.name,
    ssh_host: h.ssh_host,
    ssh_port: String(h.ssh_port),
    ssh_user: h.ssh_user,
    ssh_key_path: h.ssh_key_path,
    pve_vmid: h.pve_vmid === null ? "" : String(h.pve_vmid),
    capacity_mem_mb: String(h.capacity_mem_mb),
    enabled: h.enabled,
  };
}

const EMPTY_HOST_FORM: HostForm = {
  name: "",
  ssh_host: "",
  ssh_port: "22",
  ssh_user: "",
  ssh_key_path: "",
  pve_vmid: "",
  capacity_mem_mb: "8192",
  enabled: true,
};

function validateHostForm(f: HostForm): Record<string, string> {
  const e: Record<string, string> = {};
  if (!HOST_NAME_RE.test(f.name)) e.name = "1-64 chars: letters, numbers, ., _ or -";
  if (!SSH_HOST_RE.test(f.ssh_host)) e.ssh_host = "1-253 chars: letters, numbers, ., _ or -";
  const port = Number(f.ssh_port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) e.ssh_port = "1-65535";
  if (!SSH_USER_RE.test(f.ssh_user)) e.ssh_user = "1-32 chars: letters, numbers, ., _ or -";
  if (!SSH_KEY_PATH_RE.test(f.ssh_key_path)) e.ssh_key_path = "Absolute path (starts with /)";
  if (f.pve_vmid !== "") {
    const vmid = Number(f.pve_vmid);
    if (!Number.isInteger(vmid) || vmid < 1 || vmid > 999999999) e.pve_vmid = "Integer 1-999999999";
  }
  const cap = Number(f.capacity_mem_mb);
  if (!Number.isInteger(cap) || cap < 128 || cap > 262144) e.capacity_mem_mb = "128-262144";
  return e;
}

function HostsTab() {
  const { data: hosts, loading, error, refetch } = useAsync<AdminAppHost[]>(
    () => apiFetch<AdminAppHost[]>("/api/admin/app-hosts"),
    []
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminAppHost | null>(null);
  const [form, setForm] = useState<HostForm>(EMPTY_HOST_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [switching, setSwitching] = useState<Set<number>>(new Set());

  const [orphansHost, setOrphansHost] = useState<AdminAppHost | null>(null);
  const [orphans, setOrphans] = useState<AdminOrphansResponse | null>(null);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphansError, setOrphansError] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState<string | null>(null);

  function openCreate() {
    setEditTarget(null);
    setForm(EMPTY_HOST_FORM);
    setErrors({});
    setFormOpen(true);
  }

  function openEdit(h: AdminAppHost) {
    setEditTarget(h);
    setForm(hostFormFrom(h));
    setErrors({});
    setFormOpen(true);
  }

  async function loadOrphans(h: AdminAppHost) {
    setOrphansLoading(true);
    setOrphansError(null);
    try {
      const res = await apiFetch<AdminOrphansResponse>(`/api/admin/app-hosts/${h.id}/orphans`);
      setOrphans(res);
    } catch (err) {
      setOrphans(null);
      setOrphansError(
        friendlyError(err, "Couldn't scan the host for orphaned projects. Try again in a moment.")
      );
    } finally {
      setOrphansLoading(false);
    }
  }

  function openOrphans(h: AdminAppHost) {
    setOrphansHost(h);
    setOrphans(null);
    setOrphansError(null);
    void loadOrphans(h);
  }

  async function toggleEnabled(h: AdminAppHost, enabled: boolean) {
    setSwitching((prev) => new Set(prev).add(h.id));
    try {
      await apiPatch(`/api/admin/app-hosts/${h.id}`, { enabled });
      notifications.show({
        color: "green",
        message: enabled
          ? `${h.name} enabled — new app deploys may land on it.`
          : `${h.name} disabled — new app deploys won't land on it.`,
      });
      refetch();
    } catch (err) {
      adminErrorToast(err, "Couldn't update the host.");
    } finally {
      setSwitching((prev) => {
        const next = new Set(prev);
        next.delete(h.id);
        return next;
      });
    }
  }

  async function submitHost(e: FormEvent) {
    e.preventDefault();
    const errs = validateHostForm(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      if (editTarget === null) {
        await apiPost("/api/admin/app-hosts", {
          name: form.name.trim(),
          ssh_host: form.ssh_host.trim(),
          ssh_port: Number(form.ssh_port),
          ssh_user: form.ssh_user.trim(),
          ssh_key_path: form.ssh_key_path.trim(),
          pve_vmid: form.pve_vmid === "" ? null : Number(form.pve_vmid),
          capacity_mem_mb: Number(form.capacity_mem_mb),
          enabled: form.enabled,
        });
        notifications.show({ color: "green", message: "App host created" });
      } else {
        // Send only changed fields — the backend refuses ANY ssh_* field on
        // an in-use host, so echoing unchanged values would false-409.
        const patch: Record<string, unknown> = {};
        if (form.name.trim() !== editTarget.name) patch.name = form.name.trim();
        if (form.ssh_host.trim() !== editTarget.ssh_host) patch.ssh_host = form.ssh_host.trim();
        if (Number(form.ssh_port) !== editTarget.ssh_port) patch.ssh_port = Number(form.ssh_port);
        if (form.ssh_user.trim() !== editTarget.ssh_user) patch.ssh_user = form.ssh_user.trim();
        if (form.ssh_key_path.trim() !== editTarget.ssh_key_path) {
          patch.ssh_key_path = form.ssh_key_path.trim();
        }
        const pve = form.pve_vmid === "" ? null : Number(form.pve_vmid);
        if (pve !== editTarget.pve_vmid) patch.pve_vmid = pve;
        if (Number(form.capacity_mem_mb) !== editTarget.capacity_mem_mb) {
          patch.capacity_mem_mb = Number(form.capacity_mem_mb);
        }
        if (form.enabled !== editTarget.enabled) patch.enabled = form.enabled;
        if (Object.keys(patch).length > 0) {
          await apiPatch(`/api/admin/app-hosts/${editTarget.id}`, patch);
          notifications.show({ color: "green", message: "App host updated" });
        }
      }
      setFormOpen(false);
      setEditTarget(null);
      refetch();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409 && apiErr.error === "app_host_name_taken") {
        setErrors({ name: "A host with this name already exists" });
      } else if (apiErr.status === 409 && apiErr.error === "app_host_in_use") {
        notifications.show({
          color: "red",
          message:
            "Host still has app instances — its SSH target can't be changed while instances exist.",
        });
      } else {
        adminErrorToast(apiErr, "Couldn't save the app host.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDelete(h: AdminAppHost) {
    modals.openConfirmModal({
      title: "Delete app host",
      centered: true,
      children: (
        <Text size="sm">
          Delete app host <b>{h.name}</b>? This cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await apiDelete(`/api/admin/app-hosts/${h.id}`);
          notifications.show({ color: "green", message: "App host deleted" });
          refetch();
        } catch (err) {
          const apiErr = err as ApiError;
          if (apiErr.status === 409 && apiErr.error === "app_host_in_use") {
            notifications.show({
              color: "red",
              message:
                "Host still has app instances (failed rows count) — remove those first.",
            });
          } else {
            adminErrorToast(apiErr, "Couldn't delete the app host.");
          }
        }
      },
    });
  }

  function confirmCleanup(h: AdminAppHost, project: string) {
    modals.openConfirmModal({
      title: "Clean up orphaned project",
      centered: true,
      children: (
        <Text size="sm">
          Remove orphaned project <b>{project}</b> from <b>{h.name}</b>? Its network and
          project directory are deleted from the host. This cannot be undone.
        </Text>
      ),
      labels: { confirm: "Clean up", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        setCleaning(project);
        try {
          await apiPost(`/api/admin/app-hosts/${h.id}/orphans/cleanup`, { project });
          notifications.show({ color: "green", message: `Orphan ${project} cleaned up` });
          if (orphansHost) void loadOrphans(orphansHost);
        } catch (err) {
          adminErrorToast(err, "Couldn't clean up the orphaned project.");
        } finally {
          setCleaning(null);
        }
      },
    });
  }

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text size="sm" c="dimmed">
          Docker app hosts that run customer apps.
        </Text>
        <Button onClick={openCreate}>Add host</Button>
      </Group>

      {error && (
        <Text c="red" mb="sm">
          {error}
        </Text>
      )}
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Host</Table.Th>
            <Table.Th>Enabled</Table.Th>
            <Table.Th>Capacity</Table.Th>
            <Table.Th>Instances</Table.Th>
            <Table.Th>Health</Table.Th>
            <Table.Th>Disk free</Table.Th>
            <Table.Th w={130}>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={8}>
                <Text c="dimmed">Loading…</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && hosts && hosts.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={8}>
                <Text c="dimmed">No app hosts yet.</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {hosts?.map((h) => (
            <Table.Tr key={h.id}>
              <Table.Td>{h.name}</Table.Td>
              <Table.Td style={{ fontFamily: "monospace" }}>
                {h.ssh_host}:{h.ssh_port}
              </Table.Td>
              <Table.Td>
                <Switch
                  size="sm"
                  checked={h.enabled}
                  disabled={switching.has(h.id)}
                  onChange={(e) => void toggleEnabled(h, e.currentTarget.checked)}
                  aria-label={`Enable ${h.name}`}
                />
              </Table.Td>
              <Table.Td>{h.capacity_mem_mb} MB</Table.Td>
              <Table.Td>{h.instances}</Table.Td>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  <ThemeIcon
                    size={10}
                    color={h.probe.reachable ? "green" : "red"}
                    variant="filled"
                    radius="xl"
                    style={{ padding: 0, minWidth: 10, height: 10 }}
                  />
                  <Text size="sm">{h.probe.reachable ? "reachable" : "—"}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                {h.disk ? `${h.disk.free_mb.toLocaleString()} MB` : "—"}
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Tooltip label="Edit host" position="bottom" withArrow>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      onClick={() => openEdit(h)}
                      aria-label="Edit host"
                    >
                      <IconPencil size={18} stroke={1.75} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Orphaned projects" position="bottom" withArrow>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      onClick={() => openOrphans(h)}
                      aria-label="Orphaned projects"
                    >
                      <IconBug size={18} stroke={1.75} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete host" position="bottom" withArrow>
                    <ActionIcon
                      variant="default"
                      color="red"
                      size="lg"
                      onClick={() => confirmDelete(h)}
                      aria-label="Delete host"
                    >
                      <IconTrash size={18} stroke={1.75} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        title={editTarget === null ? "Add app host" : `Edit app host — ${editTarget.name}`}
        centered
        size="lg"
      >
        <form onSubmit={submitHost}>
          <Stack gap="sm">
            <Group grow align="flex-start">
              <TextInput
                label="Name"
                placeholder="apps-host-01"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
                error={errors.name}
              />
              <TextInput
                label="SSH host"
                placeholder="192.168.23.24"
                value={form.ssh_host}
                onChange={(e) => setForm({ ...form, ssh_host: e.currentTarget.value })}
                error={errors.ssh_host}
              />
            </Group>
            <Group grow align="flex-start">
              <NumberInput
                label="SSH port"
                value={form.ssh_port}
                onChange={(v) => setForm({ ...form, ssh_port: String(v ?? "") })}
                min={1}
                max={65535}
                allowDecimal={false}
                error={errors.ssh_port}
              />
              <TextInput
                label="SSH user"
                placeholder="sam"
                value={form.ssh_user}
                onChange={(e) => setForm({ ...form, ssh_user: e.currentTarget.value })}
                error={errors.ssh_user}
              />
            </Group>
            <TextInput
              label="SSH key path"
              description="Absolute path to the private key, as seen by the portal container."
              placeholder="/run/secrets/apps_host_key"
              value={form.ssh_key_path}
              onChange={(e) => setForm({ ...form, ssh_key_path: e.currentTarget.value })}
              error={errors.ssh_key_path}
            />
            <Group grow align="flex-start">
              <NumberInput
                label="PVE VMID (optional)"
                description="Informational link to a Proxmox guest."
                value={form.pve_vmid}
                onChange={(v) => setForm({ ...form, pve_vmid: String(v ?? "") })}
                min={1}
                max={999999999}
                allowDecimal={false}
                error={errors.pve_vmid}
              />
              <NumberInput
                label="Capacity (MB RAM)"
                description="Total memory bookable by app instances on this host."
                value={form.capacity_mem_mb}
                onChange={(v) => setForm({ ...form, capacity_mem_mb: String(v ?? "") })}
                min={128}
                max={262144}
                allowDecimal={false}
                error={errors.capacity_mem_mb}
              />
            </Group>
            <Switch
              label="Enabled"
              description="Disabled hosts are skipped for NEW app deploys; existing instances keep running."
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.currentTarget.checked })}
            />
            <Button type="submit" loading={submitting} mt="xs">
              {editTarget === null ? "Create host" : "Save changes"}
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={orphansHost !== null}
        onClose={() => setOrphansHost(null)}
        title={`Orphaned projects — ${orphansHost?.name ?? ""}`}
        centered
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Compose projects found on the host that no app instance claims. Clean up removes
            the project&apos;s network and directory from the host.
          </Text>
          {orphansLoading && (
            <Text c="dimmed" size="sm">
              Scanning…
            </Text>
          )}
          {orphansError && (
            <Alert color="red" variant="light" p="sm">
              {orphansError}
            </Alert>
          )}
          {!orphansLoading && !orphansError && orphans !== null && orphans.orphans.length === 0 && (
            <Text c="dimmed" size="sm">
              No orphaned projects.
            </Text>
          )}
          {orphans !== null && orphans.orphans.length > 0 && (
            <Stack gap="xs">
              {orphans.orphans.map((o) => (
                <Group key={o.project} justify="space-between" wrap="nowrap">
                  <Text size="sm" ff="monospace" truncate style={{ flex: 1 }}>
                    {o.project}
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    loading={cleaning === o.project}
                    onClick={() => orphansHost && confirmCleanup(orphansHost, o.project)}
                  >
                    Clean up
                  </Button>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      </Modal>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Instances tab
// ---------------------------------------------------------------------------

const FORCE_STOP_BLOCKED = new Set(["stopped", "deleting", "updating"]);

function InstancesTab({ active }: { active: boolean }) {
  const [instances, setInstances] = useState<AdminAppInstance[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>("all");
  const [stopping, setStopping] = useState<Set<number>>(new Set());

  async function load() {
    try {
      const rows = await apiFetch<AdminAppInstance[]>("/api/admin/app-instances");
      setInstances(rows);
      setError(null);
    } catch (err) {
      setError(friendlyError(err, "Could not load app instances."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll only while the Instances tab is showing and the page is visible.
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, INSTANCES_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  function confirmForceStop(inst: AdminAppInstance) {
    modals.openConfirmModal({
      title: "Force stop app instance",
      centered: true,
      children: (
        <Text size="sm">
          Force stop <b>{inst.name}</b> (customer <b>{inst.username}</b>)? Its containers are
          stopped regardless of the customer&apos;s own state gating — this is the operator
          break-glass.
        </Text>
      ),
      labels: { confirm: "Force stop", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        setStopping((prev) => new Set(prev).add(inst.id));
        try {
          await apiPost(`/api/admin/app-instances/${inst.id}/force-stop`);
          notifications.show({ color: "green", message: "Instance force stopped" });
          await load();
        } catch (err) {
          const apiErr = err as ApiError;
          if (apiErr.status === 409 && apiErr.error === "app_invalid_state") {
            notifications.show({
              color: "red",
              message: "Instance can't be force-stopped while deleting or updating.",
            });
          } else {
            adminErrorToast(apiErr, "Couldn't force stop the instance.");
          }
        } finally {
          setStopping((prev) => {
            const next = new Set(prev);
            next.delete(inst.id);
            return next;
          });
        }
      },
    });
  }

  const statuses = [...new Set((instances ?? []).map((i) => i.status))].sort();
  const filtered = (instances ?? []).filter(
    (i) => statusFilter === "all" || statusFilter === null || i.status === statusFilter
  );

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text size="sm" c="dimmed">
          All customer app instances across hosts.
        </Text>
        <Select
          placeholder="Filter by status"
          w={220}
          data={[
            { value: "all", label: "All statuses" },
            ...statuses.map((s) => ({ value: s, label: appStatusCopy(s) })),
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </Group>

      {error && (
        <Alert color="red" variant="light" mb="sm" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}
      <ScrollArea>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Template</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Host</Table.Th>
              <Table.Th>URL</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th w={130}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && instances === null && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="dimmed">Loading…</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {!loading && instances !== null && filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="dimmed">No app instances.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.map((inst) => (
              <Table.Tr key={inst.id}>
                <Table.Td>{inst.username}</Table.Td>
                <Table.Td>{inst.name}</Table.Td>
                <Table.Td style={{ fontFamily: "monospace" }}>{inst.template_slug}</Table.Td>
                <Table.Td>
                  <StatusPill status={inst.status} label={appStatusCopy(inst.status)} />
                </Table.Td>
                <Table.Td>{inst.host ?? "—"}</Table.Td>
                <Table.Td>
                  {inst.subdomain ? (
                    <Anchor
                      href={`https://${inst.subdomain}.${APPS_DOMAIN}`}
                      target="_blank"
                      rel="noreferrer"
                      size="sm"
                    >
                      <Group gap={4} wrap="nowrap">
                        {`${inst.subdomain}.${APPS_DOMAIN}`}
                        <IconExternalLink size={14} stroke={1.75} />
                      </Group>
                    </Anchor>
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{formatShortDateTime(inst.updated_at)}</Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    leftSection={<IconPlayerStop size={14} stroke={1.75} />}
                    disabled={FORCE_STOP_BLOCKED.has(inst.status) || stopping.has(inst.id)}
                    loading={stopping.has(inst.id)}
                    onClick={() => confirmForceStop(inst)}
                  >
                    Force stop
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Box>
  );
}
